import dayjs from 'dayjs';
import { LOGGER_LOG_TYPE, LOGGER_MESSAGE_TYPE, LOGGER_PRINT_LOGS, LOGGER_PRINT_LOGS_LEVEL, LOGGER_SEND_LOGS, LOGGER_SEND_LOGS_LEVEL, SERVER_BASE_URL, STORAGE } from '../../Config';
import Http from './Http';
import Storage from 'common/services/Storage';
import { UserProfile } from 'api/account/models/UserProfile';

class Logger {
    private systemName = '';
    private systemVersion = '';
    private appVersion = '';
    private url = SERVER_BASE_URL;
    private userId?: number;
    private userProfile?: UserProfile;

    public setUserId(userId: number) {
        this.userId = userId;
    }

    public setUserProfile(profile: UserProfile | null) {
        this.userProfile = profile || undefined;
    }

    private extractResponseBody(extraData: any): string {
        const responseData = extraData?.response?.data ?? extraData?.error?.response?.data;
        if (responseData === undefined || responseData === null || responseData === '') {
            return '';
        }
        try {
            return typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
        } catch {
            return '';
        }
    }

    private extractStackTrace(extraData: any): string {
        if (extraData instanceof Error) {
            return extraData.stack || '';
        }
        if (extraData?.error instanceof Error) {
            return extraData.error.stack || '';
        }
        if (extraData?.stack) {
            return extraData.stack;
        }
        if (extraData?.error?.stack) {
            return extraData.error.stack;
        }
        return '';
    }

    public error(logType: LOGGER_LOG_TYPE, message: string, extraData?: any, allowSend = true): void {
        this.log(LOGGER_MESSAGE_TYPE.ERROR, logType, message, extraData, allowSend);
    }

    public info(logType: LOGGER_LOG_TYPE, message: string, extraData?: any, allowSend = true): void {
        this.log(LOGGER_MESSAGE_TYPE.INFO, logType, message, extraData, allowSend);
    }

    private log(messageType: LOGGER_MESSAGE_TYPE, logType: LOGGER_LOG_TYPE, message: string, extraData?: any, allowSend = true) {
        const data = {
            type: LOGGER_MESSAGE_TYPE[messageType],
            logType: LOGGER_LOG_TYPE[logType],
            date: dayjs.utc(new Date()).format('DD/MM/YYYY HH:mm:ss'),
            systemName: this.systemName,
            systemVersion: this.systemVersion,
            appVersion: this.appVersion,
            userId: this.userId,
            message,
            extraData,
        };

        if (LOGGER_PRINT_LOGS && messageType >= LOGGER_PRINT_LOGS_LEVEL) {
            const logMessage = `[${data.date}] ${data.message}: ${data.extraData && data.extraData.message ? data.extraData.message : ''}`;
            switch (messageType) {
                case LOGGER_MESSAGE_TYPE.INFO:
                    console.info(logMessage, data);
                    break;
                case LOGGER_MESSAGE_TYPE.ERROR:
                    console.info(logMessage, data);
                    break;
                default:
                    console.log(logMessage, data);
                    break;
            }
        }

        if (allowSend && LOGGER_SEND_LOGS  && messageType >= LOGGER_SEND_LOGS_LEVEL) {
            const responseBody = this.extractResponseBody(extraData);
            const logMessage = `[${data.date}] ${data.message}: ${data.extraData && data.extraData.message ? data.extraData.message : ''}`
                + (responseBody ? ` — ${responseBody.substring(0, 300)}` : '');
            const profile = this.userProfile || Storage.getObject(STORAGE.AUTH_USER_PROFILE);
            const stackTrace = this.extractStackTrace(extraData);

            // Enriquecer dados com informacao do utilizador (valores planos/strings apenas)
            const enrichedData: Record<string, string> = {
                url: window.location.href || '',
                userId: profile?.id || '',
                userName: profile?.userName || '',
                userEmail: profile?.email || '',
                companyId: profile?.companyId || '',
                userRoles: profile?.roles?.join(', ') || '',
                userAgent: navigator.userAgent || '',
                logType: LOGGER_LOG_TYPE[logType] || ''
            };

            // Adicionar URL do request se existir
            if (extraData?.url) {
                enrichedData.requestUrl = String(extraData.url);
            }

            // Adicionar status code se existir
            const statusCode = extraData?.response?.status ?? extraData?.error?.response?.status;
            if (statusCode) {
                enrichedData.statusCode = String(statusCode);
            }

            // Adicionar corpo da resposta do backend se existir (causa real dos 400)
            if (responseBody) {
                enrichedData.responseBody = responseBody.substring(0, 2000);
            }

            Http.log(logMessage, profile, enrichedData, stackTrace);
        }
    }
}

export default new Logger();
